import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SurveysService, type QuestionAlternative, type QuestionAlternativeCreate, type QuestionAlternativeUpdate } from "@/lib/api_client";
import { getAuthHeaders } from "@/lib/api";
import { fetchAll } from "./fetchAll";

export const useQuestionAlternatives = () =>
  useQuery({
    queryKey: ["question-alternatives"],
    queryFn: () => fetchAll<QuestionAlternative>(SurveysService.getApiQuestionAlternatives),
    staleTime: 5 * 60_000,
  });

export const useCreateQuestionAlternative = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: QuestionAlternativeCreate) => {
      const res = await SurveysService.postApiQuestionAlternatives({ body: data, headers: getAuthHeaders() });
      if (!res.data) throw new Error("Failed");
      return res.data as unknown as QuestionAlternative;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["question-alternatives"] }),
  });
};

export const useUpdateQuestionAlternative = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: QuestionAlternativeUpdate }) => {
      const res = await SurveysService.putApiQuestionAlternativesById({ path: { id }, body: data, headers: getAuthHeaders() });
      if (!res.data) throw new Error("Failed");
      return res.data as unknown as QuestionAlternative;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["question-alternatives"] }),
  });
};

export const useDeleteQuestionAlternative = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await SurveysService.deleteApiQuestionAlternativesById({ path: { id }, headers: getAuthHeaders() });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["question-alternatives"] }),
  });
};
